/**
 * Dish-type classification at import time, plus the telemetry that makes it
 * measurable.
 *
 * Why this exists: the classifier used to run on exactly one import path. The
 * email/batch path went through api/recipes/import.ts and got classified;
 * everything a user added by hand — URL, text paste, photo — was written
 * straight to Firestore with no dishTypes at all. The saved corpus showed it
 * plainly: 28% unclassified via email-import, 86% via app-import.
 *
 * Worse, the URL path hardcoded `recipeCategory: ''`, discarding the
 * publisher's own schema.org category that the scraper had already extracted.
 * Measured over 500 cached scrapes: 83% carry a category, and keeping it moves
 * sync classification from 36% to 96%. That is a bigger win than any model
 * call, for no latency and no API round trip.
 *
 * The sync layers are pure (src/utils/dishTypeVocabulary), so the client can
 * run them itself. Only the Gemini fallback needs a server, and that stays on
 * the import API where it already lives.
 *
 * Every path reports `recipe_classification_predicted`, which is what makes
 * "does Paprika get better at this over time?" answerable in PostHog:
 * unclassified rate and correction rate, broken down by import path and by
 * which layer decided.
 */

import { classifyDishTypeSync } from '../utils/dishTypeVocabulary';
import { trackEvent } from './analyticsService';

/** Where the recipe came from — the dimension the metrics break down by. */
export type ImportPath = 'app-import' | 'text-import' | 'photo-import' | 'email-import';

export interface ClassifyOnImportParams {
  importPath: ImportPath;
  title: string;
  /** schema.org recipeCategory — string or array, per the spec. */
  rawCategory?: unknown;
  sourceUrl?: string;
}

/**
 * Classify with the sync layers and report the outcome.
 *
 * Returns the `dishTypes` array to persist — empty when nothing was confident,
 * which is deliberate: an unclassified recipe surfaces in the quality checker,
 * where correcting it feeds the few-shot examples. A wrong guess would not.
 */
export const classifyOnImport = (params: ClassifyOnImportParams): string[] => {
  const { importPath, title, rawCategory, sourceUrl } = params;

  const result = classifyDishTypeSync({
    title,
    // classifyDishTypeSync wraps this in an array; canonicalDishTypes flattens
    // and skips non-strings, so an array category is safe to pass straight in.
    rawCategory: rawCategory as string | undefined,
    sourceUrl,
  });

  reportClassification({
    importPath,
    dishType: result?.dishType ?? null,
    source: result?.source ?? null,
    confidence: result?.confidence ?? null,
    hadRawCategory: hasCategory(rawCategory),
  });

  return result ? [result.dishType] : [];
};

const hasCategory = (raw: unknown): boolean => {
  if (Array.isArray(raw)) return raw.some(v => typeof v === 'string' && v.trim().length > 0);
  return typeof raw === 'string' && raw.trim().length > 0;
};

export interface ReportClassificationParams {
  importPath: ImportPath;
  dishType: string | null;
  /** null when nothing classified it. */
  source: string | null;
  confidence: number | null;
  hadRawCategory: boolean;
  /** How many few-shot examples primed the model. Server paths only. */
  fewShotCount?: number;
}

/**
 * Emit `recipe_classification_predicted`.
 *
 * Split out from classifyOnImport because the email/batch path classifies on
 * the server — api/recipes/import.ts returns the result and the client reports
 * it here, since PostHog is client-side only in this app.
 *
 * `source: 'none'` is recorded rather than skipped. The unclassified case is
 * the denominator of the metric that matters; dropping it would make the
 * numbers look better precisely when the classifier is doing worst.
 */
export const reportClassification = (params: ReportClassificationParams): void => {
  trackEvent('recipe_classification_predicted', {
    import_path: params.importPath,
    classified: params.dishType !== null,
    source: params.source ?? 'none',
    dish_type: params.dishType,
    confidence: params.confidence,
    had_raw_category: params.hadRawCategory,
    ...(params.fewShotCount !== undefined && { few_shot_count: params.fewShotCount }),
  });
};
